import { DriftStatus } from '@/types'
import { TableFilterMenu, type FilterGroup } from '@/components/carbon/TableFilterMenu'

/**
 * DriftRefinePopover — the secondary filters (status, the statuses the open
 * queue hides, detected-between dates with All time / 7 days / 30 days quick
 * ranges) as the Carbon table filter (components/carbon/TableFilterMenu): a funnel
 * icon button in the queue table's toolbar that opens a menu of status
 * checkboxes and a date range. Severity slicing (the common case) lives in
 * the toolbar's own Severity select; this menu holds the rare-case knobs so
 * the table doesn't need a separate filter card above it.
 *
 * Status accepts several values at once: they travel as a comma list in
 * `filters.status`, which DriftManagementTab's query already splits.
 *
 * The legacy default-export `DriftFilters` is intentionally removed —
 * `DriftManagementTab` no longer renders it. Anyone still importing it
 * should switch to `DriftRefinePopover` rendered inside the table toolbar.
 */

export interface DriftFilterValues {
  tenantId: string
  severity: string
  status: string
  fromDate?: string
  toDate?: string
}

/**
 * The open queue's optional "Hide" group. The Open tab always leaves out
 * Remediated (those rows live in Back to baseline), so that is no filter and
 * never counts on the filter button; hiding Awaiting remediation is the one
 * real choice, off by default, and Reset turns it off.
 */
export interface HiddenStatuses {
  awaiting: boolean
  onChange: (next: { awaiting: boolean }) => void
}

interface Props {
  filters: DriftFilterValues
  onFilterChange: (filters: DriftFilterValues) => void
  hidden?: HiddenStatuses
}

const HIDE_AWAITING = 'awaiting'
const hideOptions = [{ value: HIDE_AWAITING, label: 'Awaiting remediation' }]

const statusOptions = [
  { value: DriftStatus.New, label: 'New' },
  { value: DriftStatus.Pending, label: 'Pending' },
  { value: DriftStatus.PendingApproval, label: 'Pending approval' },
  { value: DriftStatus.Approved, label: 'Approved' },
  { value: DriftStatus.AwaitingRemediation, label: 'Awaiting remediation' },
  { value: DriftStatus.Rejected, label: 'Rejected' },
  { value: DriftStatus.Remediated, label: 'Remediated' },
  { value: DriftStatus.Ignored, label: 'Ignored' },
]

function splitStatuses(status: string): string[] {
  return status ? status.split(',').filter(Boolean) : []
}

export default function DriftRefinePopover({ filters, onFilterChange, hidden }: Props) {
  const clearAll = () => {
    onFilterChange({
      ...filters,
      status: '',
      fromDate: '',
      toDate: '',
    })
    hidden?.onChange({ awaiting: false })
  }

  const groups: FilterGroup[] = [
    {
      legend: 'Status',
      // The open view never holds Remediated rows, so it offers no Remediated status.
      options: hidden ? statusOptions.filter((o) => o.value !== DriftStatus.Remediated) : statusOptions,
      selected: splitStatuses(filters.status),
      onChange: (selected) => onFilterChange({ ...filters, status: selected.join(',') }),
    },
  ]
  if (hidden) {
    groups.push({
      legend: 'Hide',
      options: hideOptions,
      selected: hidden.awaiting ? [HIDE_AWAITING] : [],
      onChange: (selected) => hidden.onChange({ awaiting: selected.includes(HIDE_AWAITING) }),
    })
  }

  return (
    <TableFilterMenu
      label="Filter drifts"
      groups={groups}
      dateRange={{
        legend: 'Detected between',
        presets: true,
        from: filters.fromDate || '',
        to: filters.toDate || '',
        onChange: ({ from, to }) => onFilterChange({ ...filters, fromDate: from, toDate: to }),
      }}
      onReset={clearAll}
    />
  )
}
